import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import contestAbi from "../abi/CatContest.json";
import catCoinAbi from "../abi/CatCoin.json";
import nftAbi from "../abi/CatNFT.json";
import governorAbi from "../abi/SimpleGovernor.json";

const CatContest = () => {
  const [cats, setCats] = useState([]);
  const [catName, setCatName] = useState("");
  const [catImage, setCatImage] = useState("");
  const [catVotes, setCatVotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState("Loading...");
  const [contestId, setContestId] = useState(null);
  const [viewMode, setViewMode] = useState("current"); // current | past
  const [remaining, setRemaining] = useState(0);

  const contestAddr = process.env.REACT_APP_CONTEST_CONTRACT;
  const tokenAddr = process.env.REACT_APP_TOKEN_CONTRACT;
  const nftAddr = process.env.REACT_APP_NFT_CONTRACT;
  const governorAddr = process.env.REACT_APP_GOVERNOR_CONTRACT;

  const loadContest = async (targetId) => {
    try {
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const contest = new ethers.Contract(contestAddr, contestAbi, provider);
      const nft = new ethers.Contract(nftAddr, nftAbi, provider);

      const current = await contest.currentContestId();
      const id = targetId ?? Number(current) - 1;
      setContestId(id);

      const info = await contest.contests(id);
      const endTime = Number(info.endTime);
      const finalized = info.finalized;
      const now = Math.floor(Date.now() / 1000);
      const totalSec = Math.max(0, endTime - now);
      setRemaining(totalSec);

      if (!info.startTime || Number(info.startTime) === 0) {
        setStatus(`❌ No contest created yet 💤`);
      } else if (now < Number(info.startTime)) {
        setStatus(`🕓 Contest #${id} has not started yet`);
      } else if (now >= Number(info.startTime) && now < Number(info.endTime)) {
        const remaining = Math.max(0, Math.floor((Number(info.endTime) - now) / 60));
        setStatus(`🟢 Contest #${id} is active — ${remaining} min left`);
      } else if (!info.finalized) {
        setStatus(`⏰ Contest #${id} ended — awaiting finalize`);
      } else {
        setStatus(`✅ Contest #${id} finalized`);
      }


      const tokenIds = await contest.getRegisteredTokens(id);
      const list = [];
      for (const tid of tokenIds) {
        const uri = await nft.tokenURI(tid);
        const obj = JSON.parse(uri);
        const votes = await contest.contestTokenVotes(id, tid);
        list.push({
          id: Number(tid),
          name: obj.name,
          image: obj.image,
          votes: Number(ethers.formatUnits(votes, 18)),
        });
      }
      setCats(list);
    } catch (err) {
      console.error("⚠️ Failed to load contest data:", err);
      setStatus("⚠️ Unable to load contest data");
    } finally {
      setLoading(false);
    }
  };

const proposeCreateContest = async () => {
  try {
    setProcessing(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const governor = new ethers.Contract(governorAddr, governorAbi, signer);
    const iface = new ethers.Interface(contestAbi);

    const calldata = iface.encodeFunctionData("createContest", []);

    const description = "Proposal: Create a new Cat Contest round";

    const tx = await governor.propose([contestAddr], [0], [ethers.getBytes(calldata)], description, 4);

    alert("📤 Submitting proposal to create contest...");
    await tx.wait();

    alert("✅ Proposal created successfully!");
  } catch (err) {
    console.error("❌ Proposal failed:", err);
    alert("⚠️ Failed to create proposal.\n" + (err.reason || err.message));
  } finally {
    setProcessing(false);
  }
};

const proposeFinalizeContest = async () => {
  if (!contestId) return alert("⚠️ No contest ID detected.");
  try {
    setProcessing(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const governor = new ethers.Contract(governorAddr, governorAbi, signer);
    const iface = new ethers.Interface(contestAbi);

    const calldata = iface.encodeFunctionData("finalizeContest", [contestId]);

    const description = `Proposal: Finalize Contest #${contestId}`;

    const tx = await governor.propose([contestAddr], [0], [ethers.getBytes(calldata)], description, 4);

    alert("📤 Submitting proposal to finalize contest...");
    await tx.wait();

    alert("✅ Proposal created successfully!");
  } catch (err) {
    console.error("❌ Proposal failed:", err);
    alert("⚠️ Failed to create proposal.\n" + (err.reason || err.message));
  } finally {
    setProcessing(false);
  }
};



  const submitCat = async () => {
    if (!catName || !catImage) return alert("🐱 Please enter name & image URL");
    try {
      setProcessing(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const account = await signer.getAddress();

      const token = new ethers.Contract(tokenAddr, catCoinAbi, signer);
      const nft = new ethers.Contract(nftAddr, nftAbi, signer);
      const contest = new ethers.Contract(contestAddr, contestAbi, signer);

      const txMint = await nft.mintCat(account, catName, "Contest Entry", catImage);
      const receipt = await txMint.wait();
      const mintEvent = receipt.logs.find(
        (l) => l.fragment && l.fragment.name === "CatMinted"
      );
      const tokenId = mintEvent ? Number(mintEvent.args.tokenId) : null;
      if (!tokenId) throw new Error("Token ID not found");

      const fee = ethers.parseUnits("1", 18);
      await (await token.approve(contestAddr, fee)).wait();
      await (await contest.registerForContest(tokenId)).wait();

      alert("✅ Cat submitted successfully!");
      setCatName("");
      setCatImage("");
      loadContest();
    } catch (err) {
      console.error("❌ Submit failed:", err);
      alert("❌ Failed to submit cat.\n" + (err.reason || err.message));
    } finally {
      setProcessing(false);
    }
  };

const voteForCat = async (tokenId, amountStr) => {
  if (!amountStr || isNaN(amountStr) || Number(amountStr) <= 0)
    return alert("⚠️ Please enter a valid CAT amount");

  try {
    setProcessing(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const token = new ethers.Contract(tokenAddr, catCoinAbi, signer);
    const contest = new ethers.Contract(contestAddr, contestAbi, signer);

    const amount = ethers.parseUnits(amountStr, 18);
    await (await token.approve(contestAddr, amount)).wait();
    await (await contest.vote(tokenId, amount)).wait();

    alert(`✅ Voted ${amountStr} CAT for Cat #${tokenId}`);
    loadContest(contestId);
  } catch (err) {
    console.error("⚠️ Vote failed:", err);
    alert("❌ Failed to vote.\n" + (err.reason || err.message));
  } finally {
    setProcessing(false);
  }
};


  const switchContest = (dir) => {
    const next = contestId + dir;
    if (next <= 0) return;
    loadContest(next);
  };

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [remaining]);

  useEffect(() => {
    loadContest();
  }, []);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s}s`;
  };

return (
  <div style={wrapper}>
    {/* --- 📊 Governance Proposals --- */}
    <div style={statusCard}>
      <h3 style={{ marginTop: 0 }}>📅 Current Contest Status:</h3>
      {status}
      {viewMode === "current" && remaining > 0 && (
        <p style={{ marginTop: "0.5rem", color: "#007bff" }}>
          ⏰ Time left: {formatTime(remaining)}
        </p>
      )}
      <p style={{ color: "#555", marginBottom: "1rem" }}>
      </p>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
        <button
          onClick={proposeCreateContest}
          style={{ ...btnLifecycle, backgroundColor: "#17a2b8" }}
          disabled={processing}
        >
          🏁 Propose Create Contest
        </button>
        <button
          onClick={proposeFinalizeContest}
          style={{ ...btnLifecycle, backgroundColor: "#dc3545" }}
          disabled={processing}
        >
          🎯 Propose Finalize Contest
        </button>
      </div>
    </div>

    {/* --- 🟢 / 📜 Tabs --- */}
    <div style={tabNav}>
      <button
        style={viewMode === "current" ? tabActive : tabBtn}
        onClick={() => {
          setViewMode("current");
          loadContest();
        }}
      >
        🟢 Current Contest
      </button>
      <button
        style={viewMode === "past" ? tabActive : tabBtn}
        onClick={() => setViewMode("past")}
      >
        📜 Past Contests
      </button>

      {viewMode === "past" && (
        <div style={pastBox}>
          <button style={navBtn} onClick={() => switchContest(-1)}>
            ⬅️ Prev
          </button>
          <input
            type="number"
            placeholder="Contest ID"
            value={contestId || ""}
            onChange={(e) => setContestId(Number(e.target.value))}
            style={inputShort}
          />
          <button style={navBtn} onClick={() => switchContest(1)}>
            ➡️ Next
          </button>
        </div>
      )}
    </div>

    {/* --- 😺 Submit Your Cat --- */}
    {viewMode === "current" && (
      <div style={uploadBox}>
        <h4>😺 Submit Your Cat</h4>
        {remaining > 0 ? (
          <>
            <p style={{ color: "#777", marginTop: "-5px" }}>
              Upload your cat to join the current contest!
            </p>
            <input
              type="text"
              placeholder="Cat Name"
              value={catName}
              onChange={(e) => setCatName(e.target.value)}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Image URL (e.g., https://...jpg)"
              value={catImage}
              onChange={(e) => setCatImage(e.target.value)}
              style={inputStyle}
            />
            <button onClick={submitCat} style={btnCreate} disabled={processing}>
              {processing ? "Submitting..." : "🚀 Submit Cat"}
            </button>
          </>
        ) : (
          <p style={{ color: "#999", margin: "0.5rem 0" }}>
            ⛔ Contest ended — submissions closed.
          </p>
        )}
      </div>
    )}

    {/* --- 🐱 Cat Cards --- */}
    {loading ? (
      <p>Loading...</p>
    ) : cats.length === 0 ? (
      <p>No cats submitted yet 🐱</p>
    ) : (
      <div style={catGrid}>
        {cats.map((cat) => (
          <div key={cat.id} style={catCard}>
            <div style={catRow}>
              <img
                src={cat.image || "https://placekitten.com/200/200"}
                alt={cat.name}
                style={catImg}
              />
              <div style={catInfo}>
                <h4 style={{ margin: "0 0 6px", fontWeight: "600" }}>{cat.name}</h4>
                <p style={{ margin: "0 0 12px", color: "#444" }}>Votes: {cat.votes}</p>
                <div style={voteBox}>
                  <input
                    type="number"
                    placeholder="CAT amount"
                    value={catVotes[cat.id] || ""}
                    onChange={(e) =>
                      setCatVotes({
                        ...catVotes,
                        [cat.id]: e.target.value,
                      })
                    }
                    style={voteInput}
                  />
                  <button
  onClick={() => voteForCat(cat.id)}
  style={{
    ...btnVote,
    opacity: (remaining <= 0 || status.includes("Finalized")) ? 0.5 : 1,
    cursor: (remaining <= 0 || status.includes("Finalized")) ? "not-allowed" : "pointer",
  }}
  disabled={processing || remaining <= 0 || status.includes("Finalized")}
>
  Vote
</button>

                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);



};

const wrapper = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "1.5rem",
  textAlign: "center",
};

const tabNav = {
  display: "flex",
  justifyContent: "center",
  gap: "1rem",
  marginBottom: "1.5rem",
  flexWrap: "wrap",
};

const pastBox = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
};

const navBtn = {
  border: "1px solid #ccc",
  borderRadius: "6px",
  padding: "0.4rem 0.8rem",
  cursor: "pointer",
  background: "#f0f0f0",
};

const inputShort = {
  width: "80px",
  textAlign: "center",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const statusCard = {
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: "10px",
  padding: "1rem",
  margin: "0 auto 1.5rem auto",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  maxWidth: "600px",
};

const catGrid = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.5rem",
  maxWidth: "min(90%, 720px)",
  margin: "0 auto",
};

const catCard = {
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: "12px",
  padding: "1rem 1.5rem",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  width: "100%",
  maxWidth: "600px",
};

const catRow = {
  display: "flex",
  alignItems: "center",
  gap: "1.2rem",
  justifyContent: "flex-start",
};

const catImg = {
  width: "200px",
  height: "200px",
  borderRadius: "10px",
  objectFit: "cover",
  flexShrink: 0,
  boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
};

const catInfo = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  textAlign: "left",
  flexGrow: 1,
};

const voteBox = {
  display: "flex",
  gap: "8px",
  alignItems: "center",
};

const voteInput = {
  width: "120px",
  padding: "6px",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const btnLifecycle = {
  border: "none",
  borderRadius: "6px",
  padding: "0.6rem 1.2rem",
  cursor: "pointer",
  fontWeight: "bold",
  color: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const btnVote = {
  backgroundColor: "#ffcc33", 
  color: "#333",
  border: "none",
  borderRadius: "6px",
  padding: "0.5rem 1.2rem", 
  cursor: "pointer",
  fontWeight: "bold",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
};


const uploadBox = {
  backgroundColor: "#fff8e6",
  border: "1px solid #ffd966",
  borderRadius: "10px",
  padding: "1rem",
  margin: "0 auto 1.5rem auto",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  maxWidth: "600px",
};

const btnCreate = {
  backgroundColor: "#ffcc33",   
  color: "#333",
  border: "none",
  borderRadius: "6px",
  padding: "0.6rem 1.2rem",
  cursor: "pointer",
  fontWeight: "bold",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  transition: "background 0.2s ease",
};


const inputStyle = {
  width: "100%",
  marginBottom: "10px",
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
};


const baseButton = {
  border: "none",
  borderRadius: "8px",
  padding: "0.6rem 1.4rem",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)", 
  transition: "all 0.2s ease-in-out",
};

const btnPrimary = {
  ...baseButton,
  backgroundColor: "#ffcc33",   
  color: "#333",
};
const btnPrimaryHover = {
  backgroundColor: "#ffb300",  
  boxShadow: "0 4px 10px rgba(255, 179, 0, 0.4)",
};

const btnInfo = {
  ...baseButton,
  backgroundColor: "#17a2b8",
  color: "#fff",
};
const btnInfoHover = {
  backgroundColor: "#128294",
  boxShadow: "0 4px 10px rgba(18, 130, 148, 0.5)",
};

const btnDanger = {
  ...baseButton,
  backgroundColor: "#dc3545",
  color: "#fff",
};
const btnDangerHover = {
  backgroundColor: "#b52b38",
  boxShadow: "0 4px 10px rgba(220, 53, 69, 0.5)",
};

const tabBtn = {
  ...baseButton,
  backgroundColor: "#f8f8f8",
  color: "#333",
  border: "1px solid #ccc",
};
const tabBtnHover = {
  backgroundColor: "#f0f0f0",
  boxShadow: "0 3px 8px rgba(0, 0, 0, 0.1)",
};

const tabActive = {
  ...tabBtn,
  backgroundColor: "#ffcc33",
  border: "1px solid #e0b400",
  boxShadow: "0 3px 10px rgba(255, 204, 51, 0.5)",
  fontWeight: "700",
};



export default CatContest;
